/**
 * BootScene - Loading screen with minimalist progress bar
 * Smooth transitions and kinetic typography
 */

import Phaser from 'phaser';
import { ProgressBar, ModernText } from '../ui/components';
import { UIColors, UITypography, UIAnimation } from '../ui/UIDesignTokens';
import serviceLocator, { Services } from '../core/ServiceLocator';
import eventBus from '../core/EventBus';
import ScoreManager from '../core/managers/ScoreManager';
import { AudioManager } from '../core/managers/AudioManager';
import SaveManager from '../core/managers/SaveManager';

export default class BootScene extends Phaser.Scene {
    constructor() {
        super('BootScene');
    }

    preload() {
        // Initialize services
        this.initializeServices();
        
        // Create loading UI
        this.createLoadingUI();
        
        // Load decorative assets (SVGs)
        this.loadDecorationAssets();
        
        // Simulate asset loading (since assets are procedural)
        this.simulateLoading();
    }

    loadDecorationAssets() {
        // SVG decorations stored in public/assets/decors
        this.load.svg('decor-aurora', 'assets/decors/aurora.svg', { width: 2048, height: 1024 });
        this.load.svg('decor-gear', 'assets/decors/gear.svg', { width: 1024, height: 1024 });
        this.load.svg('decor-candy', 'assets/decors/candy.svg', { width: 1024, height: 1024 });
        this.load.svg('decor-coral', 'assets/decors/coral.svg', { width: 1024, height: 1024 });
        this.load.svg('decor-temple', 'assets/decors/temple.svg', { width: 1024, height: 1024 });
    }

    initializeServices() {
        // Register EventBus
        serviceLocator.register(Services.EVENT_BUS, eventBus);
        
        // Register managers
        serviceLocator.register(Services.SCORE_MANAGER, new ScoreManager());
        serviceLocator.register(Services.AUDIO_MANAGER, new AudioManager());
        serviceLocator.register(Services.SAVE_MANAGER, new SaveManager());
    }

    createLoadingUI() {
        const { width, height } = this.cameras.main;
        
        // Dark background
        this.add.rectangle(width / 2, height / 2, width, height, 0x0a0a0f);
        
        // Animated ball (loading indicator)
        this.createLoadingBall(width, height);
        
        // Title text (kinetic entrance)
        this.titleText = this.add.text(width / 2, height / 2 - 80, 'BIG BLUE BALL', {
            fontSize: '42px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightBold,
            fill: '#ffffff'
        }).setOrigin(0.5).setAlpha(0);
        
        // Animate title in
        this.tweens.add({
            targets: this.titleText,
            alpha: 1,
            y: height / 2 - 100,
            duration: 800,
            ease: 'Power2.easeOut'
        });
        
        // Loading text
        this.loadingText = this.add.text(width / 2, height / 2 + 70, 'Loading...', {
            fontSize: '16px',
            fontFamily: UITypography.fontFamily,
            fontStyle: UITypography.weightMedium,
            fill: '#8E8E93'
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: this.loadingText,
            alpha: 1,
            delay: 300,
            duration: 500
        });
        
        // Progress bar
        this.progressBar = new ProgressBar(this, width / 2, height / 2 + 110, {
            width: 250,
            height: 4,
            fillColor: UIColors.primary,
            glowColor: UIColors.primaryLight,
            showGlow: true
        });
        this.progressBar.setAlpha(0);
        
        this.tweens.add({
            targets: this.progressBar.container,
            alpha: 1,
            delay: 400,
            duration: 500
        });
        
        // Loading tips
        this.createLoadingTips(width, height);
    }

    createLoadingBall(width, height) {
        const ballY = height / 2 - 20;
        
        // Ball shadow
        this.ballShadow = this.add.ellipse(width / 2, ballY + 50, 40, 10, 0x007AFF, 0.2);
        
        // Main ball
        this.ball = this.add.circle(width / 2, ballY, 30, 0x007AFF);
        this.ball.setStrokeStyle(2, 0x5AC8FA);
        
        // Highlight
        this.ballHighlight = this.add.circle(width / 2 - 8, ballY - 8, 8, 0x66b3ff, 0.8);
        
        // Bouncing animation
        this.tweens.add({
            targets: [this.ball, this.ballHighlight],
            y: ballY - 25,
            duration: 500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
        
        // Shadow sync
        this.tweens.add({
            targets: this.ballShadow,
            scaleX: 0.7,
            scaleY: 0.5,
            duration: 500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
        
        // Rotation effect on ball
        this.tweens.add({
            targets: this.ballHighlight,
            angle: 360,
            duration: 3000,
            repeat: -1
        });
    }

    createLoadingTips(width, height) {
        const tips = [
            '💡 Hold DOWN to magnetically pull boxes!',
            '💡 Press SHIFT to dash horizontally',
            '💡 Double jump to reach higher platforms',
            '💡 Push the box onto the button to open the door',
            '💡 Collect all stars for the best time!',
            '💡 Press R to quickly restart the level'
        ];
        
        const randomTip = tips[Math.floor(Math.random() * tips.length)];
        
        this.tipText = this.add.text(width / 2, height - 80, randomTip, {
            fontSize: '14px',
            fontFamily: UITypography.fontFamily,
            fill: '#636366'
        }).setOrigin(0.5).setAlpha(0);
        
        this.tweens.add({
            targets: this.tipText,
            alpha: 1,
            delay: 600,
            duration: 500
        });
    }

    simulateLoading() {
        let progress = 0;
        const loadingSteps = [
            { progress: 0.15, text: 'Initializing physics...', delay: 200 },
            { progress: 0.35, text: 'Loading levels...', delay: 400 },
            { progress: 0.55, text: 'Creating worlds...', delay: 300 },
            { progress: 0.75, text: 'Preparing adventures...', delay: 350 },
            { progress: 0.90, text: 'Almost ready...', delay: 250 },
            { progress: 1.00, text: 'Let\'s go!', delay: 200 }
        ];
        
        let currentStep = 0;
        
        const processStep = () => {
            if (currentStep >= loadingSteps.length) {
                this.onLoadComplete();
                return;
            }
            
            const step = loadingSteps[currentStep];
            
            // Update progress bar
            if (this.progressBar) {
                this.progressBar.setProgress(step.progress);
            }
            
            // Update loading text
            if (this.loadingText) {
                this.loadingText.setText(step.text);
            }
            
            currentStep++;
            this.time.delayedCall(step.delay, processStep);
        };
        
        // Start loading sequence
        this.time.delayedCall(500, processStep);
    }

    onLoadComplete() {
        const { width, height } = this.cameras.main;
        
        // Stop ball animation
        this.tweens.killTweensOf([this.ball, this.ballHighlight, this.ballShadow]);
        
        // Victory animation
        this.tweens.add({
            targets: [this.ball, this.ballHighlight],
            y: height / 2 - 60,
            scaleX: 1.2,
            scaleY: 1.2,
            duration: 300,
            ease: 'Back.easeOut',
            onComplete: () => {
                // Fade out and transition
                this.time.delayedCall(400, () => {
                    this.cameras.main.fadeOut(600, 0, 0, 0);
                    
                    this.time.delayedCall(600, () => {
                        this.scene.start('MenuScene');
                    });
                });
            }
        });
        
        // Update text
        if (this.loadingText) {
            this.loadingText.setText('Ready!');
            this.loadingText.setColor('#34C759');
        }
    }

    create() {
        // Scene transitions handled in simulateLoading
    }
}
